
import { Helmet } from "react-helmet";
import { getStructuredData, type StructuredDataProps } from "@/utils/structuredData";

interface SEOMetadataProps extends StructuredDataProps {
  metaTitle: string;
  metaDescription: string;
  canonicalUrl?: string;
  keywords?: string;
  articleSchema?: boolean;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  preloadImages?: string[]; 
  preconnect?: string[];
  deferStyles?: string[];
  category?: string;
  robotsDirective?: string;
}

const SEOMetadata = ({
  metaTitle,
  metaDescription,
  imageUrl,
  pageName,
  isHomepage = false,
  primaryLink = "",
  canonicalUrl,
  title,
  description,
  keywords,
  articleSchema = false,
  datePublished,
  dateModified,
  author = "Jörg Hörmann",
  preloadImages = [],
  preconnect = [],
  deferStyles = [],
  category,
  robotsDirective
}: SEOMetadataProps) => {
  const baseUrl = "https://joerg-hoermann.de";
  const structuredData = getStructuredData({
    title: metaTitle,
    description: metaDescription,
    imageUrl,
    pageName,
    isHomepage,
    primaryLink
  });

  // Default keywords
  const defaultKeywords = "Finanzcoaching, Katalysator-Führung, Business Consulting, Jörg Hörmann, Jörg Hormann, Führungskräfte-Coaching";
  const keywordsToUse = keywords ? `${defaultKeywords}, ${keywords}` : defaultKeywords;

  // Event Handler für defer styles
  const handleStyleLoad = (e: React.SyntheticEvent<HTMLLinkElement, Event>) => {
    if (e.currentTarget) {
      e.currentTarget.media = "all";
    }
  };

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywordsToUse} />
      {robotsDirective && <meta name="robots" content={robotsDirective} />}
      
      {/* Favicon */}
      <link rel="icon" type="image/png" sizes="32x32" href="/images/jh-favicon.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/images/jh-favicon.png" />
      <link rel="apple-touch-icon" href="/images/jh-favicon.png" />
      <link rel="shortcut icon" href="/images/jh-favicon.png" />
      <meta name="theme-color" content="#2E5090" />
      
      {/* Performance optimizations - no Google Fonts preconnect */}
      {preconnect
        .filter(url => !url.includes('fonts.googleapis') && !url.includes('fonts.gstatic'))
        .map((url) => (
          <link key={url} rel="preconnect" href={url} crossOrigin="anonymous" />
        ))}
      
      {/* Preload critical images */}
      {preloadImages.map((imgUrl) => (
        <link 
          key={imgUrl} 
          rel="preload" 
          as="image" 
          href={imgUrl.startsWith("http") ? imgUrl : `${baseUrl}${imgUrl}`} 
          fetchPriority="high"
        />
      ))}
      
      {/* Defer non-critical CSS */}
      {deferStyles.map((styleUrl) => (
        <link 
          key={styleUrl} 
          rel="stylesheet" 
          href={styleUrl} 
          media="print" 
          onLoad={handleStyleLoad}
        />
      ))}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={articleSchema ? "article" : "website"} />
      <meta property="og:url" content={canonicalUrl || (isHomepage ? baseUrl : `${baseUrl}${primaryLink || ""}`)} />
      {imageUrl && <meta property="og:image" content={imageUrl.startsWith("http") ? imageUrl : `${baseUrl}${imageUrl}`} />}
      {imageUrl && <meta property="og:image:width" content="1200" />}
      {imageUrl && <meta property="og:image:height" content="630" />}
      {imageUrl && <meta property="og:image:alt" content={metaTitle} />}
      <meta property="og:site_name" content="Jörg Hörmann - Finanzcoaching & Consulting" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl && <meta property="twitter:image" content={imageUrl.startsWith("http") ? imageUrl : `${baseUrl}${imageUrl}`} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl || (isHomepage ? baseUrl : `${baseUrl}${primaryLink || ""}`)} />
      
      {/* LLMs.txt für KI-Crawler */}
      <link rel="alternate" type="text/plain" title="LLM Summary" href="/llms.txt" />
      <link rel="alternate" type="text/plain" title="LLM Full Summary" href="/llms-full.txt" />
      
      {/* Hreflang for German-language site */}
      <link rel="alternate" hrefLang="de-DE" href={canonicalUrl || (isHomepage ? baseUrl : `${baseUrl}${primaryLink || ""}`)} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl || (isHomepage ? baseUrl : `${baseUrl}${primaryLink || ""}`)} />
      
      {/* Mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="format-detection" content="telephone=no" />
      {!robotsDirective && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      <meta name="author" content={author} />
      <meta name="language" content="German" />
      <meta httpEquiv="Content-Language" content="de" />
      <meta property="og:locale" content="de_DE" />
      
      {/* Article schema */}
      {articleSchema && datePublished && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": description,
            "image": imageUrl,
            "author": {
              "@type": "Person",
              "name": author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Jörg Hörmann Finanzcoaching",
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/images/jh-logo-dark.png`
              }
            },
            "datePublished": datePublished,
            "dateModified": dateModified || datePublished,
            "mainEntityOfPage": canonicalUrl,
            "articleSection": category || "Coaching"
          })}
        </script>
      )}
      
      {/* Structured data - skip empty arrays */}
      {Array.isArray(structuredData) && structuredData.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      {!Array.isArray(structuredData) && structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOMetadata;
